import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PdfBuilderEmbed } from "./pdf-builder-embed";
import type { BuilderEmbed } from "./pdf-builder-embed";
import type { RouterOutputs } from "@/trpc";

type OrgForm = RouterOutputs["organizationForm"]["listForms"][number];

type Step =
  | { kind: "builder"; embed: BuilderEmbed }
  | { kind: "no-builder" }
  | { kind: "publishing" }
  | { kind: "done" };

interface PdfFormBuilderDialogProps {
  /** The PDF form to remap. null means the dialog is closed. */
  form: OrgForm | null;
  organizationBusinessUnitId: string;
  onOpenChange: (open: boolean) => void;
}

export function PdfFormBuilderDialog({
  form,
  organizationBusinessUnitId,
  onOpenChange,
}: PdfFormBuilderDialogProps) {
  const trpc = useTRPC();
  const [step, setStep] = useState<Step | null>(null);

  const getBuilderUrlMutation = useMutation(
    trpc.organizationForm.getPdfBuilderUrl.mutationOptions({
      onError: (err) => toast.error(err.message || "Failed to open builder"),
    }),
  );

  const syncFieldsMutation = useMutation(
    trpc.organizationForm.syncPdfFields.mutationOptions(),
  );

  const publishMutation = useMutation(
    trpc.organizationForm.publishPdfForm.mutationOptions(),
  );

  const handleOpen = async () => {
    if (!form) {
      return;
    }
    const result = await getBuilderUrlMutation.mutateAsync({
      organizationBusinessUnitId,
      formId: form.id,
    });
    setStep({ kind: "builder", embed: result.embed });
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(null);
    }, 300);
  };

  const handleImportAndPublish = async (previousStep: Step) => {
    if (!form) {
      return;
    }
    setStep({ kind: "publishing" });

    let synced;
    try {
      synced = await syncFieldsMutation.mutateAsync({
        organizationBusinessUnitId,
        formId: form.id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to sync fields";
      toast.error(message);
      setStep(previousStep);
      return;
    }

    const fieldCount = (synced?.fields ?? []).length;
    if (fieldCount === 0) {
      toast.error(
        "No fields found. Add at least one field to the PDF in the builder, then try again.",
      );
      setStep(previousStep);
      return;
    }

    try {
      await publishMutation.mutateAsync({
        organizationBusinessUnitId,
        formId: form.id,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to publish form";
      toast.error(message);
      setStep(previousStep);
      return;
    }

    await queryClient.invalidateQueries(
      trpc.organizationForm.listForms.queryOptions({
        organizationBusinessUnitId,
      }),
    );
    toast.success("PDF form remapped and published");
    setStep({ kind: "done" });
  };

  const isBusy =
    getBuilderUrlMutation.isPending ||
    syncFieldsMutation.isPending ||
    publishMutation.isPending;

  // When the dialog opens (form transitions from null to a value), start the
  // flow by fetching the builder URL.
  const prevFormId = React.useRef<string | null>(null);
  if (form && form.id !== prevFormId.current && !step) {
    prevFormId.current = form.id;
    handleOpen();
  }
  if (!form) {
    prevFormId.current = null;
  }

  function renderContent() {
    if (!step || getBuilderUrlMutation.isPending) {
      return (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="text-sm text-gray-500">Loading builder...</p>
        </div>
      );
    }

    switch (step.kind) {
      case "builder":
        return (
          <div className="flex flex-col flex-1 min-h-0 gap-4">
            <p className="text-sm text-gray-600 shrink-0">
              Remap the PDF fields below. When done, click{" "}
              <strong>Done — Re-import Fields</strong> to sync the changes.
              Every field must have a real label and be marked required.
            </p>
            <div className="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200">
              <PdfBuilderEmbed embed={step.embed} />
            </div>
            <DialogFooter className="shrink-0">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleImportAndPublish(step)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Done — Re-import Fields"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "no-builder":
        return (
          <div className="space-y-4">
            <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              Map the fields in DocuSeal, then click{" "}
              <strong>Sync Fields</strong> to import them. Every field must have
              a real label and be marked required.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleImportAndPublish(step)}
                disabled={isBusy}
              >
                {isBusy ? (
                  <>
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  "Sync Fields"
                )}
              </Button>
            </DialogFooter>
          </div>
        );

      case "publishing":
        return (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            <p className="text-sm text-gray-500">
              Importing fields and publishing...
            </p>
          </div>
        );

      case "done":
        return (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="text-base font-medium text-gray-800">
              PDF form remapped and published!
            </p>
          </div>
        );
    }
  }

  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className={
          step?.kind === "builder"
            ? "h-[90vh] flex flex-col overflow-hidden"
            : "w-[calc(100vw-2rem)] max-w-xl sm:max-h-[90vh] overflow-y-auto"
        }
        // Inline style beats the global `.dialog-content { max-width: 1000px }`.
        style={
          step?.kind === "builder"
            ? { width: "90vw", maxWidth: "none" }
            : undefined
        }
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Remap PDF Fields</DialogTitle>
          {step?.kind !== "publishing" && step?.kind !== "done" && (
            <DialogDescription>
              Re-open the visual editor to update the field mapping for &ldquo;
              {form?.name}&rdquo;.
            </DialogDescription>
          )}
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
